import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Check, ExternalLink, Shield, AlertCircle } from 'lucide-react';

import type { DpaVersion } from '../../api/interview';

interface DpaAcceptModalProps {
  isOpen: boolean;
  dpa: DpaVersion;
  onAccept: (version: string) => Promise<void>;
  onClose: () => void;
}

/**
 * Click-Wrap DPA / AVV acceptance modal.
 *
 * Cevee acts as the data processor for AI Interview data; the company is
 * the data controller. Acceptance is logged with IP + user-agent server-side
 * (see app.services.dpa_service.accept_dpa).
 *
 * Only the company-admin sees this modal; the backend rejects acceptance
 * from non-admin roles with 403.
 */
const DpaAcceptModal: React.FC<DpaAcceptModalProps> = ({
  isOpen,
  dpa,
  onAccept,
  onClose,
}) => {
  const { i18n } = useTranslation();
  const lang: 'de' | 'en' = i18n.language.startsWith('de') ? 'de' : 'en';

  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const t = {
    title:        lang === 'de' ? 'Datenverarbeitungsvertrag (AVV)'            : 'Data Processing Agreement (DPA)',
    intro:        lang === 'de' ? 'Bevor du AI-Interviews starten kannst, muss dein Unternehmen den AVV mit Cevee abschließen.' : 'Before you can launch AI interviews, your company must enter into a DPA with Cevee.',
    versionLabel: lang === 'de' ? 'Version'                                    : 'Version',
    summaryTitle: lang === 'de' ? 'Zusammenfassung'                            : 'Summary',
    subTitle:     lang === 'de' ? 'Eingebundene Subunternehmer'                : 'Subprocessors',
    fullLabel:    lang === 'de' ? 'Vollstaendigen Vertrag anzeigen'            : 'View full agreement',
    consent:      lang === 'de' ? 'Ich akzeptiere den AVV in Version {{v}} im Namen meines Unternehmens und bestaetige, Vertretungsberechtigung zu besitzen.' : 'I accept the DPA in version {{v}} on behalf of my company and confirm that I am authorized to do so.',
    accept:       lang === 'de' ? 'AVV akzeptieren'                            : 'Accept DPA',
    cancel:       lang === 'de' ? 'Abbrechen'                                  : 'Cancel',
    busy:         lang === 'de' ? 'Wird gespeichert...'                        : 'Saving...',
  };

  const summary = lang === 'de' ? dpa.summary_de : dpa.summary_en;
  const fullUrl = lang === 'de' ? dpa.full_text_url_de : dpa.full_text_url_en;

  const handleAccept = async () => {
    if (!accepted || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await onAccept(dpa.version);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10010] flex items-center justify-center bg-slate-900/40 backdrop-blur-md p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{t.title}</h2>
              <p className="text-xs text-slate-500">{t.versionLabel} {dpa.version}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50"
            aria-label={t.cancel}
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto space-y-5">
          <p className="text-sm text-slate-700 leading-relaxed">{t.intro}</p>

          <div>
            <h3 className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">
              {t.summaryTitle}
            </h3>
            <p className="text-sm text-slate-800 leading-relaxed bg-slate-50 rounded-lg p-4 border border-slate-100">
              {summary}
            </p>
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">
              {t.subTitle}
            </h3>
            <ul className="space-y-2">
              {dpa.subprocessors.map((s) => (
                <li key={s} className="flex items-start gap-2 text-sm text-slate-700">
                  <Check className="w-4 h-4 mt-0.5 text-emerald-500 flex-shrink-0" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>

          <a
            href={fullUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-pink-600 hover:text-pink-700"
          >
            {t.fullLabel}
            <ExternalLink className="w-3.5 h-3.5" />
          </a>

          <label className="flex items-start gap-3 p-4 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50/60 cursor-pointer transition-colors">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              disabled={submitting}
              className="w-5 h-5 mt-0.5 accent-slate-900 rounded focus:ring-slate-400"
            />
            <span className="text-sm text-slate-800 leading-relaxed">
              {t.consent.replace('{{v}}', dpa.version)}
            </span>
          </label>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900 disabled:opacity-50"
          >
            {t.cancel}
          </button>
          <button
            onClick={handleAccept}
            disabled={!accepted || submitting}
            className="px-5 py-2 text-sm font-medium text-white rounded-full bg-slate-900 hover:bg-pink-600 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {submitting ? t.busy : t.accept}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DpaAcceptModal;
